import React, {CSSProperties, ReactNode, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useReducer, useRef} from 'react'

import Button from '../buttons/Button.js'
import type {ButtonVariant} from '../buttons/styles.js'
import ProgressBar from '../feedback/ProgressBar.js'
import P from '../typography/P.js'
import {formSpacing, FormStack} from '../forms/index.js'
import socket from '../../../socketio/socketio.js'
import guid from '../../../../utils/guid.js'
import Modal from './Modal.js'
import ModalActions from './ModalActions.js'
import AlertStack from './AlertStack.js'
import PopupInputs, {PopupInput, PopupInputRefs, autoFocusTypes, getInputValues, isRequiredInputMissing} from './PopupInputs.js'
import PopupOptions, {PopupOption} from './PopupOptions.js'
import {popupStyle} from './styles.js'

export type {PopupInput, PopupOption}

/** A button of setButtonTop, setButtonBottom and setActionButtons: Button props, onClick without the event */
export interface PopupButton {
    label?: ReactNode
    variant?: ButtonVariant
    color?: string
    icon?: string
    style?: React.CSSProperties
    onClick?: () => void
    [prop: string]: any
}

export interface PopupProps {
    /** A close button in the header */
    closeButton?: boolean
    /** Escape closes the popup (default true) */
    closeOnEscape?: boolean
    /** A radio button (checkbox when multiple) in front of every option of openOptions */
    showOptionRadioButton?: boolean
    /** Instead of Bevestigen */
    confirmLabel?: ReactNode
    /** Instead of Annuleren (and Afbreken while a cancellable task runs) */
    cancelLabel?: ReactNode
    noCancelBtn?: boolean
    /** @deprecated use closeOnEscape={false} */
    noEsc?: boolean
    /** @deprecated use confirmLabel */
    confirmBtnText?: ReactNode
    /** @deprecated use cancelLabel */
    cancelBtnText?: ReactNode
}

/** The options of openAlert */
export interface PopupAlertOptions {
    /** A text button left of Sluiten that calls onAction and closes (e.g. Uitloggen) */
    actionLabel?: ReactNode
    onAction?: () => void
    /** Instead of the automatic width, e.g. popupStyle.width.question */
    width?: CSSProperties['width']
}

/** The options of openBoolean */
export interface PopupBooleanOptions {
    /** Instead of the confirmLabel prop (Bevestigen) */
    confirmLabel?: ReactNode
    /** Instead of the cancelLabel prop (Annuleren) */
    cancelLabel?: ReactNode
    /** Instead of the automatic width, e.g. popupStyle.width.question */
    width?: CSSProperties['width']
}

/** A button of openChoice */
export interface PopupChoice {
    label: ReactNode
    /** What openChoice resolves with when it is clicked */
    value: unknown
    /** Default: solid for the last choice, text for the others */
    variant?: ButtonVariant
}

/** The options of openChoice */
export interface PopupChoiceOptions {
    /** Instead of the automatic width, e.g. popupStyle.width.question */
    width?: CSSProperties['width']
}

/** The options of listenToProgress */
export interface PopupProgressOptions {
    /** Also show the message of the events below the bar (default true), false keeps the one of setProgress */
    message?: boolean
}

/** What open() resolves with on confirm: true, the {name: value} of the inputs plus batchId, or the value of setInput */
type ConfirmValue = any

/**
 * The imperative API of Popup, the type of its ref (useRef<PopupRef>). The promises of open, openOptions and openBoolean
 * resolve on confirm (or a chosen option) and also when the popup is closed without it (Cancel, Escape, the close
 * button, close()): with undefined, and false for openBoolean. openAlert resolves when it is closed in any way. Await them as `if (!await popup.open(...)) return`.
 */
export interface PopupRef {
    /**
     * Opens with title and text. Without onConfirm it returns a promise of the confirmed value: true, the values of
     * setInputs ({name: value, batchId}) or the value of setInput, undefined when closed. onClose is called on every
     * close (the popup waits while a promise it returns is pending). batchId (in the values) tags a backend batch
     * operation: its `updateProgress-${batchId}` socket events move the progress bar.
     */
    open: (title?: ReactNode, text?: ReactNode, onConfirm?: (value: ConfirmValue) => void, onClose?: (options: PopupOption[]) => unknown, noAutoFocus?: boolean, cancellable?: boolean) => Promise<ConfirmValue> | undefined
    /**
     * A message with only Sluiten (Enter closes it too), plus an optional action button left of it that calls onAction
     * and closes. Resolves when it is closed, by Sluiten, the action, Escape or close().
     */
    openAlert: (title: ReactNode, text: ReactNode, options?: PopupAlertOptions) => Promise<void>
    /**
     * Opens with a confirm button, resolves true on confirm (and closes) and false when closed. The labels fall back
     * to the confirmLabel and cancelLabel props, then Bevestigen and Annuleren.
     */
    openBoolean: (title?: ReactNode, text?: ReactNode, options?: PopupBooleanOptions) => Promise<boolean>
    /**
     * A question with a button per choice (the last one solid) and a close button: resolves with the value of the
     * clicked choice, undefined when closed without one (the close button, Escape, close()).
     */
    openChoice: (title: ReactNode, text: ReactNode, choices: PopupChoice[], options?: PopupChoiceOptions) => Promise<unknown>
    /** A list to choose from: resolves with the clicked option (or the values of the checked ones when multiple) */
    openOptions: (title?: ReactNode, text?: ReactNode, options?: (PopupOption | ReactNode)[], onConfirm?: (option: any) => void, multiple?: boolean) => Promise<any> | undefined
    /** Opens with a progress bar while task runs and closes when it is done, shows its error when it fails */
    openProgress: (title: ReactNode, text: ReactNode, task: (onProgress: (now: number, max: number) => void) => Promise<unknown>) => Promise<void>
    /** Resolves when it is closed and reset (after onClose's promise) */
    close: () => Promise<void>
    /** Instead of the confirmLabel prop (Bevestigen) for the next open, until it is closed */
    setConfirmLabel: (confirmLabel: ReactNode) => void
    /** One input, open() resolves with its value */
    setInput: (input: PopupInput) => void
    setInputs: (inputs: PopupInput[]) => void
    /** Options of the next openBoolean or open (with multiple, they are checkboxes) */
    setOptions: (options: PopupOption[], multiple?: boolean) => void
    /** The options with their selected state */
    getOptions: () => PopupOption[]
    /** Full-width buttons below the text; the popup then only has Sluiten */
    setActionButtons: (buttons: PopupButton[]) => void
    setButtonTop: (button: PopupButton) => Promise<void>
    setButtonBottom: (button: PopupButton) => Promise<void>
    /** Spinner on the confirm button and inputs disabled; setMessage/setError end it */
    setLoading: (loading: boolean) => void
    setMessage: (message: ReactNode) => void
    setWarning: (warning: ReactNode) => void
    setWarnings: (warnings: ReactNode[]) => void
    setError: (error: ReactNode) => void
    setErrors: (errors: ReactNode[]) => void
    /** A progress bar (hidden while max is 0 or empty) with an optional message below it; without a message the current one stays */
    setProgress: (now: number, max: number, message?: ReactNode) => void
    /**
     * Follows the socket's `updateProgress` events (percentage and message, e.g. of the backend's label printing)
     * until the popup closes. Call it after open: the events of an earlier task must not show up in the next one.
     */
    listenToProgress: (options?: PopupProgressOptions) => void
    /** Chooses an option, for options rendered by the caller (elements) */
    onClickOption: (option: PopupOption, index?: number) => void
}

interface PopupState {
    isOpen: boolean
    loading: boolean
    title: ReactNode
    text: ReactNode
    inputs: PopupInput[] | undefined
    /** inputs holds the one input of setInput: open() resolves with its value */
    singleInput: boolean
    buttonTop: PopupButton | null
    buttonBottom: PopupButton | null
    actionButtons: PopupButton[] | null
    options: (PopupOption | ReactNode)[]
    multiple: boolean
    progressNow: number
    progressMax: number | null
    progressMessage: ReactNode
    onConfirm: ((value: any) => void) | null
    onClose: ((options: PopupOption[]) => unknown) | null
    cancellable: boolean
    cancelling: boolean
    returnBoolean: boolean
    /** Opened by openAlert: only Sluiten and the action */
    alert: boolean
    alertAction: {label: ReactNode, onAction?: () => void} | null
    /** Opened by openChoice: its buttons instead of Bevestigen and Annuleren */
    choices: PopupChoice[] | null
    /** The labels and width of one openBoolean or openAlert, confirmLabel also of setConfirmLabel */
    confirmLabel: ReactNode
    cancelLabel: ReactNode
    width: CSSProperties['width'] | null
    message: ReactNode
    warning: ReactNode
    warnings: ReactNode[]
    error: ReactNode
    errors: ReactNode[]
}

const initialState: PopupState = {
    isOpen: false,
    loading: false,
    title: '',
    text: '',
    inputs: undefined,
    singleInput: false,
    buttonTop: null,
    buttonBottom: null,
    actionButtons: null,
    options: [],
    multiple: false,
    progressNow: 0,
    progressMax: null,
    progressMessage: '',
    onConfirm: null,
    onClose: null,
    cancellable: false,
    cancelling: false,
    returnBoolean: false,
    alert: false,
    alertAction: null,
    choices: null,
    confirmLabel: null,
    cancelLabel: null,
    width: null,
    message: '',
    warning: '',
    warnings: [],
    error: '',
    errors: []
}

// Focus on a button (Enter clicks it), a link or in a textarea (Enter is a new line) doesn't confirm
const enterIgnoredSelector = 'button, a[href], textarea, [role=button], [role=link], [contenteditable=""], [contenteditable=true]'

/**
 * A Modal for the common dialogs, driven through its ref (see PopupRef): a message with Sluiten, a question with
 * Bevestigen and Annuleren, inputs, a list of options, a progress bar, messages and errors. Enter confirms (not while loading, not
 * from a button or textarea, not while a Select in it is open), and closes a message of openAlert. The first input takes the focus on open.
 *
 * Inputs and options are kept by reference: typed values and checked options are written into the objects the caller
 * passed (input.value/values, option.selected), callers read them back.
 */
const Popup = forwardRef<PopupRef, PopupProps>(function Popup(props, ref) {
    const {closeButton, showOptionRadioButton, noCancelBtn} = props
    const closeOnEscape = props.closeOnEscape ?? !props.noEsc
    const propsConfirmLabel = props.confirmLabel ?? props.confirmBtnText
    const propsCancelLabel = props.cancelLabel ?? props.cancelBtnText

    // The state is read synchronously by the imperative methods between calls (setInputs then open, close then open),
    // so it lives in a ref and a render is requested after every change
    const stateRef = useRef<PopupState>({...initialState})
    const [, forceRender] = useReducer((count: number) => count + 1, 0)
    // Resolved after the render of the last change, for the methods that return a promise
    const rendered = useRef<(() => void)[]>([])

    const pending = useRef<{resolve: (value: unknown) => void} | null>(null)
    const batchId = useRef<string>(null)
    const progressListener = useRef<(progress: {percentage: number}) => void>(null)
    const socketProgressListener = useRef<(progress: {percentage: number, message?: string}) => void>(null)
    const inputRefs: PopupInputRefs = useRef([])
    const modalRef = useRef<HTMLDivElement>(null)
    const mounted = useRef(true)

    const update = (patch: Partial<PopupState>) => {
        stateRef.current = {...stateRef.current, ...patch}

        if (!mounted.current) {
            return Promise.resolve()
        }

        return new Promise<void>((resolve) => {
            rendered.current.push(resolve)
            forceRender()
        })
    }

    useLayoutEffect(() => {
        const callbacks = rendered.current
        rendered.current = []
        callbacks.forEach((callback) => callback())
    })

    // Settles the promise of open/openOptions/openBoolean, once
    const settle = (value: unknown) => {
        const current = pending.current
        pending.current = null
        current?.resolve(value)
    }

    // The promise of an open without a callback. An earlier promise nobody settled would hang its caller forever, it
    // ends as closed.
    const createPending = <T, >(withPromise = true) => {
        settle(stateRef.current.returnBoolean ? false : undefined)

        if (withPromise) {
            return new Promise<T>((resolve) => {
                pending.current = {resolve}
            })
        }
    }

    const stopTrackingProgress = () => {
        if (batchId.current && progressListener.current) {
            socket.off(`updateProgress-${batchId.current}`, progressListener.current)
        }

        progressListener.current = null

        if (socketProgressListener.current) {
            socket.off('updateProgress', socketProgressListener.current)
        }

        socketProgressListener.current = null
    }

    const trackProgress = () => {
        stopTrackingProgress()

        // Generated on every open so callers can tag a backend batch operation with it (it is in the confirmed values)
        // and get live `updateProgress-${batchId}` updates without wiring up their own listener
        batchId.current = guid.generate()
        progressListener.current = ({percentage}) => {
            update({isOpen: true, progressNow: percentage, progressMax: 100})
        }

        socket.on(`updateProgress-${batchId.current}`, progressListener.current)
    }

    useEffect(() => {
        mounted.current = true

        return () => {
            mounted.current = false
            stopTrackingProgress()
        }
    }, [])

    const finishClose = () => {
        stopTrackingProgress()
        inputRefs.current = []

        return update({...initialState})
    }

    const close = () => {
        const {returnBoolean, onClose, options} = stateRef.current

        settle(returnBoolean ? false : undefined)

        // onClose may return a promise (e.g. waiting for the backend to confirm a cancellation): the popup stays open
        // until it resolves, so no results meant for it can arrive and reopen it after it's gone
        const onCloseResult = typeof onClose === 'function' ? onClose(options as PopupOption[]) : undefined

        if (onCloseResult && typeof (onCloseResult as Promise<unknown>).then === 'function') {
            update({cancelling: true})

            return (onCloseResult as Promise<unknown>).then(finishClose)
        }

        return finishClose()
    }

    const getConfirmValue = (): ConfirmValue => {
        const {options, inputs, singleInput} = stateRef.current

        if (inputs && singleInput) {
            return inputs[0]?.value
        }

        if (inputs) {
            return {...getInputValues(inputs), batchId: batchId.current}
        }

        if (options.length) {
            return (options as PopupOption[]).filter((option) => option?.selected).map((option) => option.value)
        }

        return true
    }

    const confirm = () => {
        if (isRequiredInputMissing(stateRef.current.inputs)) {
            return
        }

        const {onConfirm, returnBoolean} = stateRef.current
        const value = getConfirmValue()

        // A yes/no question has nothing left to show after the answer
        if (returnBoolean && typeof onConfirm !== 'function') {
            settle(true)
            close()
            return
        }

        update({loading: true, message: '', error: ''})

        if (typeof onConfirm === 'function') {
            onConfirm(value)
        } else {
            settle(value)
        }
    }

    const onClickOption = (option: PopupOption, index?: number) => {
        const {multiple, options, onConfirm} = stateRef.current

        if (multiple) {
            const option = options[index] as PopupOption
            option.selected = !option.selected

            update({options: [...options]})
            return
        }

        if (typeof onConfirm === 'function') {
            // Closed first: onConfirm may open the popup again for a next step
            close()
            onConfirm(option)
        } else {
            settle(option)
            close()
        }
    }

    const setInputValue = (index: number, value: any) => {
        const inputs = [...stateRef.current.inputs]
        const input = inputs[index]

        if (Array.isArray(value)) {
            input.values = value
        } else if (input.type === 'radio') {
            // Radio inputs sharing a name are mutually exclusive: keep them all in sync on the selected value so a
            // dependent input's `disabled` function can read the group's value from any one of them
            inputs.forEach((other) => {
                if (other.type === 'radio' && other.name === input.name) {
                    other.value = value
                }
            })
        } else {
            input.value = value
        }

        update({inputs})
    }

    const focusFirstInput = () => {
        setTimeout(() => {
            const {inputs} = stateRef.current
            const index = (inputs || []).findIndex((input) => autoFocusTypes.includes(input.type) && !input.disabled)

            inputRefs.current[index]?.focus?.()
        }, 0)
    }

    const api: PopupRef = {
        open: (title, text, onConfirm, onClose, noAutoFocus, cancellable) => {
            trackProgress()

            const promise = createPending(!onConfirm)

            // Not loading: after a confirm the caller may ask a next question in the same popup
            update({isOpen: true, loading: false, title, text, onConfirm: onConfirm || null, onClose: onClose || null, cancellable: !!cancellable})

            if (!noAutoFocus) {
                focusFirstInput()
            }

            return promise
        },
        openAlert: (title, text, {actionLabel, onAction, width} = {}) => {
            const promise = createPending<void>()

            update({isOpen: true, loading: false, title, text, alert: true, alertAction: actionLabel ? {label: actionLabel, onAction} : null, width: width ?? null})

            return promise
        },
        openBoolean: (title, text, {confirmLabel, cancelLabel, width} = {}) => {
            const promise = createPending<boolean>()

            update({isOpen: true, loading: false, title, text, returnBoolean: true, confirmLabel: confirmLabel ?? null, cancelLabel: cancelLabel ?? null, width: width ?? null})

            return promise.then((value) => value !== false && value !== undefined)
        },
        openChoice: (title, text, choices, {width} = {}) => {
            const promise = createPending()

            update({isOpen: true, loading: false, title, text, choices, width: width ?? null})

            return promise
        },
        openOptions: (title, text, options, onConfirm, multiple = false) => {
            const promise = createPending(!onConfirm)

            update({isOpen: true, loading: false, title, text, options, onConfirm: onConfirm || null, multiple})

            return promise
        },
        openProgress: async (title, text, task) => {
            update({isOpen: true, title, text, loading: true})

            try {
                await task((progressNow, progressMax) => {
                    // Don't reopen the popup when it was closed while the task is running
                    if (stateRef.current.isOpen && progressMax > 0) {
                        update({progressNow, progressMax})
                    }
                })

                await close()
            } catch (err) {
                update({isOpen: true, error: err?.message || err, loading: false})
            }
        },
        close,
        setConfirmLabel: (confirmLabel) => {
            update({confirmLabel})
        },
        setInput: (input) => {
            update({inputs: [input], singleInput: true})
        },
        setInputs: (inputs) => {
            update({inputs, singleInput: false})
        },
        setOptions: (options, multiple = false) => {
            update({options, multiple})
        },
        getOptions: () => stateRef.current.options as PopupOption[],
        setActionButtons: (actionButtons) => {
            update({actionButtons})
        },
        setButtonTop: (buttonTop) => update({buttonTop}),
        setButtonBottom: (buttonBottom) => update({buttonBottom}),
        setLoading: (loading) => {
            update({loading})
        },
        setMessage: (message) => {
            update({isOpen: true, message, loading: false})
        },
        setWarning: (warning) => {
            update({isOpen: true, warning})
        },
        setWarnings: (warnings) => {
            update({warnings})
        },
        // A progress message would read as if the task still runs
        setError: (error) => {
            update({isOpen: true, error, loading: false, progressMessage: ''})
        },
        setErrors: (errors) => {
            update({isOpen: true, errors, loading: false, progressMessage: ''})
        },
        setProgress: (progressNow, progressMax, progressMessage) => {
            update({isOpen: true, progressNow, progressMax, ...(progressMessage !== undefined && {progressMessage})})
        },
        listenToProgress: ({message = true} = {}) => {
            if (socketProgressListener.current) {
                socket.off('updateProgress', socketProgressListener.current)
            }

            // The events carry no id: they are sent to this browser only, for the task it runs. Not reopening, the
            // listener is removed on close.
            socketProgressListener.current = (progress) => {
                update({progressNow: progress.percentage, progressMax: 100, ...(message && {progressMessage: progress.message})})
            }

            socket.on('updateProgress', socketProgressListener.current)
        },
        onClickOption
    }

    // Every method works on the refs, so the handle made on the first render stays valid
    useImperativeHandle(ref, () => api, [])

    const state = stateRef.current
    const {isOpen, title, text, inputs, singleInput, options, multiple, actionButtons, buttonTop, buttonBottom, progressNow, progressMax, progressMessage, loading, cancellable, cancelling, alert, alertAction, choices, message, warning, warnings, error, errors} = state
    const confirmLabel = state.confirmLabel ?? propsConfirmLabel
    const cancelLabel = state.cancelLabel ?? propsCancelLabel

    const hasActionButtons = !!actionButtons?.length
    const showsResult = !!message || !!error || errors.length > 0
    const canConfirm = !alert && !choices && !showsResult && !options.length && !hasActionButtons

    // Enter confirms while the confirm button is shown, see enterIgnoredSelector
    const onKeyDownRef = useRef<(event: KeyboardEvent) => void>(null)
    onKeyDownRef.current = (event) => {
        const active = document.activeElement
        const inPopup = !active || active === document.body || !!modalRef.current?.contains(active)
        const selectOpen = inputRefs.current.some((input) => typeof input?.focused === 'function' && input.focused())

        if (event.key === 'Enter' && alert && inPopup && !active?.closest?.(enterIgnoredSelector)) {
            close()
            return
        }

        if (event.key !== 'Enter' || !canConfirm || loading || cancelling || !inPopup || selectOpen || active?.closest?.(enterIgnoredSelector)) {
            return
        }

        // CurrencyInput and MetricInput only pass their value up on blur or Enter. Blur the focused input: its onBlur
        // commits synchronously, so the typed value is in the inputs when confirming
        if (active instanceof HTMLInputElement) {
            active.blur()
        }

        confirm()
    }

    useEffect(() => {
        if (!isOpen) {
            return
        }

        const onKeyDown = (event: KeyboardEvent) => onKeyDownRef.current(event)

        document.addEventListener('keydown', onKeyDown)

        return () => document.removeEventListener('keydown', onKeyDown)
    }, [isOpen])

    const onClickButton = (button: PopupButton) => {
        update({loading: true, error: '', message: ''})
        button.onClick?.()
    }

    const onClickChoice = (choice: PopupChoice) => {
        settle(choice.value)
        close()
    }

    const onClickAlertAction = () => {
        const onAction = alertAction?.onAction

        // Closed first: the action may open the popup again
        close()
        onAction?.()
    }

    const width = state.width != null ? state.width : errors.length > 0 ? popupStyle.width.errors : (inputs?.length > 0 && !singleInput) ? popupStyle.width.inputs : popupStyle.width.narrow

    return (
        <Modal
            ref={modalRef}
            show={isOpen}
            closeOnEscape={closeOnEscape}
            title={title || 'Foutmelding'}
            closeButton={closeButton || !!choices}
            style={{width, maxWidth: popupStyle.maxWidth}}
            onClose={close}
        >
            {text &&
                <P style={{marginBottom: popupStyle.textGap, whiteSpace: 'pre-wrap'}}>{text}</P>
            }

            {buttonTop &&
                <Button
                    {...buttonTop}
                    variant={buttonTop.variant || 'outline'}
                    style={{width: '100%', marginBottom: popupStyle.blockGap, ...buttonTop.style}}
                    loading={loading}
                    onClick={() => onClickButton(buttonTop)}
                />
            }

            {inputs?.length > 0 &&
                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <PopupInputs inputs={inputs} loading={loading} onChange={setInputValue} inputRefs={inputRefs} />
                </FormStack>
            }

            {hasActionButtons && actionButtons.map((button, index) => (
                <Button
                    key={`actionButton${index}`}
                    {...button}
                    variant={button.variant || 'outline'}
                    style={{width: '100%', marginBottom: popupStyle.blockGap, ...button.style}}
                    loading={loading}
                    onClick={() => onClickButton(button)}
                />
            ))}

            {options.length > 0 &&
                <PopupOptions options={options} multiple={multiple} showOptionRadioButton={showOptionRadioButton} onClickOption={onClickOption} />
            }

            {!!progressMax &&
                <ProgressBar style={{marginBottom: popupStyle.progressGap}} now={progressNow} max={progressMax} />
            }

            {progressMessage &&
                <P style={{textAlign: 'center', marginBottom: popupStyle.progressMessageGap}}>{progressMessage}</P>
            }

            <AlertStack message={message} warning={warning} warnings={warnings} error={error} errors={errors} />

            <ModalActions>
                {buttonBottom &&
                    <Button {...buttonBottom} onClick={() => onClickButton(buttonBottom)} />
                }

                {alertAction &&
                    <Button variant='text' style={{minWidth: popupStyle.buttonMinWidth}} onClick={onClickAlertAction}>
                        {alertAction.label}
                    </Button>
                }

                {choices?.map((choice, index) => (
                    <Button
                        key={`choice${index}`}
                        variant={choice.variant || (index === choices.length - 1 ? 'solid' : 'text')}
                        style={{minWidth: popupStyle.buttonMinWidth}}
                        onClick={() => onClickChoice(choice)}
                    >
                        {choice.label}
                    </Button>
                ))}

                {alert &&
                    <Button variant='solid' style={{minWidth: popupStyle.buttonMinWidth}} onClick={close}>
                        Sluiten
                    </Button>
                }

                {!noCancelBtn && canConfirm &&
                    <Button variant='text' style={{minWidth: popupStyle.buttonMinWidth}} disabled={(loading && !cancellable) || cancelling} onClick={close}>
                        {loading && cancellable ? cancelLabel || 'Afbreken' : cancelLabel || 'Annuleren'}
                    </Button>
                }

                {multiple && !actionButtons &&
                    <Button
                        style={{width: '100%', marginTop: 12}}
                        label='Bevestigen'
                        onClick={() => {
                            confirm()
                            close()
                        }}
                    />
                }

                {canConfirm &&
                    <Button variant='text' style={{minWidth: popupStyle.buttonMinWidth}} loading={loading} disabled={isRequiredInputMissing(inputs)} onClick={confirm}>
                        {confirmLabel || 'Bevestigen'}
                    </Button>
                }

                {(showsResult || hasActionButtons) &&
                    <Button variant='text' onClick={close}>
                        Sluiten
                    </Button>
                }
            </ModalActions>
        </Modal>
    )
})

export default Popup
